import { DatePicker } from 'antd';
import { EmptyAnalyticIcon, FilterIcon } from 'assets/icon';
import CategoryDropdown from 'components/CategoryDropdown';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  downloadAnalytics,
  downloadAnalyticsLeaderboard,
  downloadGazeAnalytics,
  fetchAnalytics,
  fetchGazeLocations,
  fetchLeaderboard,
  fetchProgrammaticAnalytics,
} from 'state/actions/analytic';

import { ExportIcon } from 'assets/icon/dynamic';
import AnalyticReport from 'components/AnalyticReport';
import FilterDropdown from 'components/FilterDropdown';
import GazeAnalyticReport from 'components/GazeAnalyticReport';
import LeaderboardReport from 'components/LeaderboardReport';
import POIRatingChart from 'components/POIRatingChart';
import ProfileAnalyzerReport from 'components/ProfileAnalyzerReport';
import ProgrammaticAnalyticReport from 'components/ProgrammaticAnalyticReport';
import VehicleAnalyticReport from 'components/VehicleAnalyticReport';
import dayjs from 'dayjs';
import _ from 'underscore';
import { fetchScreens } from 'state/actions/screens';
import { getAccess } from 'utils';
import EmotionAnalyticReport from '../../components/EmotionAnalyticReport';
import './Analytic.css';

const Analytics = () => {
  const { siteId } = useParams();

  // select report type 'poi' or 'place'
  const [typeOpen, setTypeOpen] = useState(false);
  const [selectedType, setSelectedType] = useState();
  const [tempSelectedType, setTempSelectedType] = useState(selectedType);

  const [locationOpen, setLocationOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState();
  const [tempSelectedLocation, setTempSelectedLocation] = useState([]);
  const [tempSelectedLocationDateRange, setTempSelectedLocationDateRange] =
    useState({
      startDate: null,
      endDate: null,
    });

  // select report type 'poi' or 'place'
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState();
  const [tempSelectedDevice, setTempSelectedDevice] = useState([]);
  const [access, setAccess] = useState();
  const [chartType, setChartType] = useState();

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const {
    analytics,
    loading,
    screens,
    locations,
    gazeLocations,
    userData,
    role,
  } = useSelector(
    (state) => ({
      analytics: state.analytic.data,
      loading: state.analytic.loading,
      locations: state.analytic.locations,
      gazeLocations: state.analytic.gazeLocations,
      userData: state.auth.userData,
      role: state.auth.userData.role,
      screens: state.screens.list,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  // init date range to first day of month -> today
  useEffect(() => {
    setDateRange({
      startDate: dayjs().startOf('month'),
      endDate: dayjs(),
    });
  }, []);

  useEffect(() => {
    dispatch(fetchScreens({ siteId }));
    if (userData && userData.roleAccess) {
      setAccess(getAccess(userData, 'analytic', siteId));
    }
  }, [siteId]); // eslint-disable-line
  useEffect(() => {
    if (role === 'superadmin') {
      const charts = [
        { _id: 'place', name: 'Place' },
        { _id: 'poi', name: 'POI' },
        { _id: 'category', name: 'Category' },
        { _id: 'leaderboard', name: 'Kiosk Performance' },
        { _id: 'poiratings', name: 'POI Ratings' },
        { _id: 'trafficcounter', name: 'Traffic Counter' },
        { _id: 'profileanalyzer', name: 'Profile Analyzer' },
        { _id: 'emotionanalytic', name: 'Emotion Analytic' },
        { _id: 'vehiclecounter', name: 'Vehicle Counter' },
        { _id: 'gazeanalytic', name: 'Gaze Analytic' },
        { _id: 'programmatic', name: 'Programmatic' },
      ];
      setChartType(charts);
    } else if (access) {
      const charts = [];
      if (access.wayfinding) {
        charts.push(
          { _id: 'place', name: 'Place' },
          { _id: 'poi', name: 'POI' },
          { _id: 'category', name: 'Category' },
          { _id: 'leaderboard', name: 'Kiosk Performance' },
          { _id: 'poiratings', name: 'POI Ratings' }
        );
      }

      if (access.camera) {
        charts.push(
          { _id: 'trafficcounter', name: 'Traffic Counter' },
          { _id: 'profileanalyzer', name: 'Profile Analyzer' },
          { _id: 'vehiclecounter', name: 'Vehicle Counter' },
          { _id: 'emotionanalytic', name: 'Emotion Analytic' },
          { _id: 'gazeanalytic', name: 'Gaze Analytic' }
        );
      }

      // Programmatic needs no camera or wayfinding data — analytic.read on the
      // site (which `access` already means) is the only requirement.
      charts.push({ _id: 'programmatic', name: 'Programmatic' });

      setChartType(charts);
    }
  }, [access, role]); // eslint-disable-line
  // Load the full gaze location list once gaze is selected (stable dropdown).
  useEffect(() => {
    if (selectedType === 'gazeanalytic') {
      dispatch(fetchGazeLocations({ siteId }));
    }
  }, [selectedType, siteId]); // eslint-disable-line

  useEffect(() => {
    if (selectedType) {
      console.log('fetch analytics');
      if (selectedType === 'programmatic') {
        // Site-wide: the endpoint takes a single screenId, not the adsum id
        // list the device filter produces.
        dispatch(
          fetchProgrammaticAnalytics({
            siteId,
            start: dateRange.startDate,
            end: dateRange.endDate,
          })
        );
      } else if (selectedType === 'leaderboard') {
        console.log('leaderboard');
        dispatch(
          fetchLeaderboard({
            start: dateRange.startDate,
            end: dateRange.endDate,
            adsumScreenIds: selectedDevice,
            siteId,
          })
        );
      } else {
        dispatch(
          fetchAnalytics({
            start: dateRange.startDate,
            end: dateRange.endDate,
            type: selectedType,
            adsumScreenIds: selectedDevice,
            locations: selectedLocation,
            siteId,
          })
        );
      }
    }
  }, [selectedType, dateRange, selectedDevice, selectedLocation]); // eslint-disable-line

  useEffect(() => {
    const ids = screens.map((screen) =>
      screen.adsumScreenId ? screen.adsumScreenId : screen._id
    );
    setTempSelectedDevice([...ids]);
    setSelectedDevice([...ids]);
  }, [screens]); // eslint-disable-line

  useEffect(() => {
    if (
      selectedType === 'gazeanalytic' ||
      !locations ||
      locations.length === 0 ||
      _.isEqual(dateRange, tempSelectedLocationDateRange)
    )
      return;

    const ids = locations?.map((loc) => loc.location);

    setTempSelectedLocation(ids);
    setTempSelectedLocationDateRange(dateRange);
    setSelectedLocation(ids);
  }, [locations, dateRange]); // eslint-disable-line

  // Gaze: default selection = all locations from the stable full list.
  useEffect(() => {
    if (
      selectedType !== 'gazeanalytic' ||
      !gazeLocations ||
      gazeLocations.length === 0
    )
      return;

    // Single-select: [] = Show All, [id] = one location. Keep an existing
    // selection only if valid for gaze (not stale from another analytic type).
    const validSelection =
      selectedLocation &&
      selectedLocation.every((id) => gazeLocations.includes(id));
    if (validSelection) return;

    setTempSelectedLocation([]);
    setSelectedLocation([]);
  }, [selectedType, gazeLocations]); // eslint-disable-line

  const handleSetType = () => {
    setSelectedType(tempSelectedType);
    setTypeOpen(false);
  };
  const handleSetDevice = () => {
    setSelectedDevice(tempSelectedDevice);
    setDeviceOpen(false);
  };
  const handleSetLocation = () => {
    setSelectedLocation(tempSelectedLocation);
    setLocationOpen(false);
  };

  const getLocationLabel = () =>
    selectedLocation && selectedLocation.length > 0
      ? selectedLocation[0]
      : 'Show All';

  const getType = (type) => {
    const findCategory = chartType.find((cat) => cat._id === type);
    return findCategory?.name;
  };

  const renderComponent = () => {
    switch (selectedType) {
      case 'leaderboard':
        return <LeaderboardReport siteId={siteId} loading={loading} />;
      case 'poiratings':
        return (
          <POIRatingChart
            siteId={siteId}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        );
      case 'programmatic':
        return <ProgrammaticAnalyticReport loading={loading} />;
      case 'profileanalyzer':
        return (
          <ProfileAnalyzerReport
            siteId={siteId}
            analytics={analytics}
            type={selectedType}
            loading={loading}
            locations={locations.filter((e) =>
              selectedLocation?.includes(e.location)
            )}
          />
        );
      case 'vehiclecounter':
        return (
          <VehicleAnalyticReport
            siteId={siteId}
            analytics={analytics}
            type={selectedType}
            loading={loading}
            locations={locations}
          />
        );
      case 'gazeanalytic':
        return (
          <GazeAnalyticReport
            siteId={siteId}
            analytics={analytics}
            type={selectedType}
            loading={loading}
            locations={locations}
          />
        );
      case 'emotionanalytic':
        return (
          <EmotionAnalyticReport
            siteId={siteId}
            analytics={analytics}
            type={selectedType}
            loading={loading}
            locations={locations}
          />
        );
      default:
        return (
          <AnalyticReport
            siteId={siteId}
            loading={loading}
            type={selectedType}
            analytics={analytics}
            selectedType={
              dateRange.startDate &&
              dateRange.endDate &&
              dateRange.startDate.isSame(dateRange.endDate, 'day')
                ? 'day'
                : 'month'
            }
            onExport={() =>
              dispatch(
                downloadAnalyticsLeaderboard({
                  start: dateRange.startDate,
                  end: dateRange.endDate,
                  adsumScreenIds: selectedDevice,
                  type: selectedType,
                  siteId,
                })
              )
            }
          />
        );
    }
  };
  const cameraAnalyticList = [
    'profileanalyzer',
    'trafficcounter',
    'vehiclecounter',
    'emotionanalytic',
    'gazeanalytic',
  ];

  // Programmatic is site-wide and has no per-device filter or export endpoint.
  const noDeviceFilterList = [...cameraAnalyticList, 'programmatic', 'poiratings'];

  const handleDateChange = (dates) => {
    const startDayjs = dates?.[0];
    const endDayjs = dates?.[1];

    setDateRange({
      startDate: startDayjs,
      endDate: endDayjs,
    });
  };

  return (
    <>
      <div className="analytic">
        <div className="analytic-header">
          <div className="analytic-header-left">
            {chartType && (
              <div
                className="playlist-wrapper"
                onClick={() => !loading && setTypeOpen(!typeOpen)}
              >
                <div className="playlist-selection">
                  <FilterIcon />
                  <span>{!selectedType ? 'Type' : getType(selectedType)}</span>
                </div>
                <i
                  className={`feather-chevron-down ${
                    typeOpen ? 'playlist-open' : 'playlist-closed'
                  }`}
                />

                <CategoryDropdown
                  categories={chartType}
                  modalOpen={typeOpen}
                  handleSetPlaylist={handleSetType}
                  setTempSelectedCategory={(id) => setTempSelectedType(id)}
                  tempSelectedCategory={tempSelectedType}
                />
              </div>
            )}
            {!noDeviceFilterList.includes(selectedType) && (
              <div
                className="playlist-wrapper"
                onClick={() => !loading && setDeviceOpen(!deviceOpen)}
              >
                <div className="playlist-selection">
                  <FilterIcon />
                  <span>Show All</span>
                </div>
                <i
                  className={`feather-chevron-down ${
                    deviceOpen ? 'playlist-open' : 'playlist-closed'
                  }`}
                />

                <FilterDropdown
                  categories={screens.filter((screen) => screen.adsumScreenId)}
                  modalOpen={deviceOpen}
                  handleSetPlaylist={handleSetDevice}
                  isAdsum={screens.find((screen) => screen.adsumScreenId)}
                  setTempSelectedCategory={(id) => {
                    if (id === 'all') {
                      const categories = screens.filter(
                        (scr) => scr.adsumScreenId
                      );
                      if (tempSelectedDevice.length === categories.length) {
                        setTempSelectedDevice([]);
                      } else {
                        const all = categories.map(
                          (screen) => screen.adsumScreenId
                        );
                        console.log(all);
                        setTempSelectedDevice(all);
                      }
                    } else {
                      console.log(id);
                      const index = tempSelectedDevice.indexOf(id);
                      if (index === -1) {
                        setTempSelectedDevice([...tempSelectedDevice, id]);
                      } else {
                        tempSelectedDevice.splice(index, 1);
                        setTempSelectedDevice([...tempSelectedDevice]);
                      }
                    }
                  }}
                  filter
                  tempSelectedCategory={tempSelectedDevice}
                />
              </div>
            )}
            {selectedType === 'gazeanalytic' && (
              <div
                className="playlist-wrapper"
                onClick={() => !loading && setLocationOpen(!locationOpen)}
              >
                <div className="playlist-selection">
                  <FilterIcon />
                  <span>{getLocationLabel()}</span>
                </div>
                <i
                  className={`feather-chevron-down ${
                    locationOpen ? 'playlist-open' : 'playlist-closed'
                  }`}
                />

                <CategoryDropdown
                  categories={gazeLocations.map((loc) => ({
                    _id: loc,
                    name: loc,
                  }))}
                  modalOpen={locationOpen}
                  title="Location"
                  handleSetPlaylist={handleSetLocation}
                  setTempSelectedCategory={(id) =>
                    setTempSelectedLocation(id === 'all' ? [] : [id])
                  }
                  filter
                  tempSelectedCategory={
                    tempSelectedLocation.length > 0
                      ? tempSelectedLocation[0]
                      : 'all'
                  }
                />
              </div>
            )}

            <div>
              <DatePicker.RangePicker
                className="analytic-date-picker"
                value={[dateRange.startDate, dateRange.endDate]}
                onChange={handleDateChange}
              />
            </div>
          </div>
          <div className="analytic-header-right">
            {selectedType && selectedType !== 'programmatic' && (
              <button
                className="btn-outline-primary"
                onClick={() =>
                  dispatch(
                    selectedType === 'gazeanalytic'
                      ? downloadGazeAnalytics({
                          start: dateRange.startDate,
                          end: dateRange.endDate,
                          locations: selectedLocation,
                          siteId,
                        })
                      : downloadAnalytics({
                          start: dateRange.startDate,
                          end: dateRange.endDate,
                          adsumScreenIds: selectedDevice,
                          type: selectedType,
                          siteId,
                        })
                  )
                }
              >
                <ExportIcon color="#64D5D2" size={18} />
                {selectedType === 'leaderboard'
                  ? 'Export Summary'
                  : 'Export Raw Data'}
              </button>
            )}
          </div>
        </div>
        <div className="analytic-content-wrapper">
          <div className="analytic-content">
            {selectedType ? (
              renderComponent()
            ) : (
              <div className="empty-content">
                <EmptyAnalyticIcon />
                <h1 className="empty-title">
                  Select the type of data analytics
                </h1>
                <p>
                  Please select the data type first to display the analytics
                  data to be shown
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Analytics;
